import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { fireWrite } from "../../utils/offlineWrites";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import {
  useLocationTracking,
  type LocationPoint,
} from "../../contexts/LocationTrackingContext";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { useUserRoles } from "../../hooks/useUserRoles";
import { SUPER_ADMIN_ROLE } from "../../config/userRoles";
import "./UserPopover.css";

const SRC_LABEL: Record<LocationPoint["src"], string> = {
  phone: "phone",
  watch: "watch",
  web: "this device",
};

// Watch / native-phone / web all write at most ~1 point per minute; a point
// newer than this means a device is actively sharing.
const RECENT_POINT_MS = 6 * 60_000;

/**
 * Header avatar + popover menu with theme toggle, offline status,
 * build info, and a cache-clearing reload button.
 *
 * Ported from Captain's Log.
 */
export function UserPopover() {
  const { user, userProfile, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const {
    config: trackingConfig,
    capturing,
    latestPoint,
    error: locationError,
    hasSharingCurrentTrip,
    setTrackingEnabled,
    setTrackPhone,
    setTrackWatch,
  } = useLocationTracking();
  const isOnline = useOnlineStatus();
  const { hasAnyRole, hasRole } = useUserRoles();
  const canSeeUsersLink = hasAnyRole(["manage_users", "manage_roles"]);
  const canSeeAdminLink = hasRole(SUPER_ADMIN_ROLE);
  const [open, setOpen] = useState(false);
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [locationSaveError, setLocationSaveError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const defaultLocationSharing = userProfile?.defaultLocationSharing === true;

  const trackingOn = trackingConfig.trackingEnabled;
  const trackingLive = !!latestPoint && Date.now() - latestPoint.t < RECENT_POINT_MS;
  const trackingLabel = trackingLive
    ? "Sharing your location now"
    : "Location tracking on";

  const handleToggleDefaultSharing = (value: boolean) => {
    if (!user) return;
    setLocationSaveError(null);
    // Not awaited: the ack never comes offline, which kept the checkbox
    // disabled for the whole trip. The profile listener sees the local write
    // straight away, so the checkbox moves immediately; a server rejection
    // is reported app-wide by fireWrite.
    fireWrite(
      updateDoc(doc(db, "users", user.uid), {
        defaultLocationSharing: value,
        updatedAt: serverTimestamp(),
      }),
      "location sharing preference",
    );
  };

  const runTrackingUpdate = async (fn: () => Promise<void>) => {
    if (trackingSaving) return;
    setTrackingSaving(true);
    setLocationSaveError(null);
    try {
      await fn();
    } catch (err) {
      console.error("Failed to update tracking preference:", err);
      setLocationSaveError(
        "Couldn't save — location tracking storage isn't set up for this project yet.",
      );
    } finally {
      setTrackingSaving(false);
    }
  };

  const handleToggleTracking = (value: boolean) =>
    runTrackingUpdate(() => setTrackingEnabled(value));
  const handleTogglePhone = (value: boolean) => runTrackingUpdate(() => setTrackPhone(value));
  const handleToggleWatch = (value: boolean) => runTrackingUpdate(() => setTrackWatch(value));

  const trackingStatus = (): string => {
    if (locationSaveError) return locationSaveError;
    if (locationError) return locationError;
    if (!hasSharingCurrentTrip) return "Starts automatically when you're on a trip you're sharing.";
    if (latestPoint && Date.now() - latestPoint.t < RECENT_POINT_MS) {
      const when = new Date(latestPoint.t).toLocaleTimeString();
      const where = SRC_LABEL[latestPoint.src];
      return `Sharing now — last update ${when} (${where})`;
    }
    if (capturing) return "Getting your location…";
    return "Waiting for a location fix…";
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Unregistering the service worker and emptying its caches means the next
  // load has to come from the network. Offline that's a blank screen until
  // we're back in signal, so it's refused rather than merely warned about.
  const handleReload = async () => {
    if (!isOnline) return;
    if (
      !confirm(
        "Clear the app's saved files and reload? The app will need to re-download before it works offline again.",
      )
    ) {
      return;
    }
    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } finally {
      window.location.reload();
    }
  };

  // Signing out isn't local to this browser: in the phone app it hands off to
  // the native side, which also clears the watch's sign-in. Getting either back
  // needs Google sign-in, i.e. internet — so offline it would strand the whole
  // family's devices until we're back on land.
  const handleSignOut = () => {
    if (!isOnline) return;
    if (
      !confirm(
        "Sign out? This also signs out the Trip Tracker phone app and your watch, and signing back in needs an internet connection.",
      )
    ) {
      return;
    }
    setOpen(false);
    void logout();
  };

  if (!user) return null;

  const displayName = userProfile?.displayName || user.displayName || user.email || "User";
  const avatarUrl = userProfile?.avatarUrl || user.photoURL || null;
  const initial = displayName.charAt(0).toUpperCase();

  let buildLabel = "unknown";
  try {
    buildLabel = new Date(__BUILD_TIME__).toLocaleString();
  } catch {
    // Ignore parse errors (e.g. dev mode without define).
  }

  return (
    <div className="header-user" ref={containerRef}>
      <button
        type="button"
        className="header-user-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open user menu"
      >
        {trackingOn && (
          <span
            className={`header-tracking-icon${trackingLive ? " live" : ""}`}
            title={trackingLabel}
            aria-label={trackingLabel}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
              />
            </svg>
          </span>
        )}
        <span className="header-user-avatar">
          {avatarUrl ? <img src={avatarUrl} alt={displayName} /> : initial}
          {!isOnline && (
            <span className="header-offline-badge" title="Offline">
              !
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="user-popover">
          <div className="user-popover-name">{displayName}</div>

          {!isOnline && (
            <div className="user-popover-offline">Offline mode — changes will sync when back online.</div>
          )}

          <div className="user-popover-divider" />
          <Link to="/entities" className="user-popover-button" onClick={() => setOpen(false)}>
            Places
          </Link>
          {canSeeUsersLink && (
            <Link to="/users" className="user-popover-button" onClick={() => setOpen(false)}>
              Users
            </Link>
          )}
          {canSeeAdminLink && (
            <Link to="/admin" className="user-popover-button" onClick={() => setOpen(false)}>
              Admin
            </Link>
          )}

          <div className="user-popover-divider" />

          <div>
            <div className="theme-selector-label">Theme</div>
            <div className="theme-selector-options">
              <button
                type="button"
                className={`theme-selector-option ${theme === "light" ? "active" : ""}`}
                onClick={() => setTheme("light")}
                title="Light"
              >
                Light
              </button>
              <button
                type="button"
                className={`theme-selector-option ${theme === "dark" ? "active" : ""}`}
                onClick={() => setTheme("dark")}
                title="Dark"
              >
                Dark
              </button>
              <button
                type="button"
                className={`theme-selector-option ${theme === "system" ? "active" : ""}`}
                onClick={() => setTheme("system")}
                title="System"
              >
                Auto
              </button>
            </div>
          </div>

          <div className="user-popover-divider" />

          <div>
            <div className="theme-selector-label">Location</div>
            <label className="popover-toggle">
              <span>Track my location</span>
              <input
                type="checkbox"
                checked={trackingConfig.trackingEnabled}
                disabled={trackingSaving}
                onChange={(e) => {
                  void handleToggleTracking(e.target.checked);
                }}
              />
            </label>
            <label className="popover-toggle">
              <span>&nbsp;&nbsp;On this phone</span>
              <input
                type="checkbox"
                checked={trackingConfig.trackPhone}
                disabled={trackingSaving || !trackingConfig.trackingEnabled}
                onChange={(e) => {
                  void handleTogglePhone(e.target.checked);
                }}
              />
            </label>
            <label className="popover-toggle">
              <span>&nbsp;&nbsp;On my watch</span>
              <input
                type="checkbox"
                checked={trackingConfig.trackWatch}
                disabled={trackingSaving || !trackingConfig.trackingEnabled}
                onChange={(e) => {
                  void handleToggleWatch(e.target.checked);
                }}
              />
            </label>
            <label className="popover-toggle">
              <span>Share with my travel parties</span>
              <input
                type="checkbox"
                checked={defaultLocationSharing}
                onChange={(e) => {
                  void handleToggleDefaultSharing(e.target.checked);
                }}
              />
            </label>
            {(trackingConfig.trackingEnabled || locationSaveError) && (
              <div className="popover-hint">{trackingStatus()}</div>
            )}
          </div>

          <div className="user-popover-divider" />

          <button
            type="button"
            className="user-popover-button"
            onClick={handleSignOut}
            disabled={!isOnline}
          >
            Sign Out
          </button>
          {!isOnline && (
            <div className="popover-hint">
              Sign-out is off while offline — signing back in needs an internet connection.
            </div>
          )}

          <button
            type="button"
            className="user-popover-button"
            onClick={() => {
              void handleReload();
            }}
            disabled={!isOnline}
            title="Clear cache and reload"
          >
            Clear cache & reload
          </button>
          {!isOnline && (
            <div className="popover-hint">
              Unavailable offline — the app couldn't reload without a connection.
            </div>
          )}

          <div className="user-popover-build">Build: {buildLabel}</div>
        </div>
      )}
    </div>
  );
}
